import { useState, useEffect } from 'react'
import {
  transcribeVideo,
  getTaskStatus,
  getTranscript,
  parseTranscript,
  stopTranscription,
} from '../lib/api'
import {
  TranscriptionRequest,
  TranscriptionResponse,
  TranscriptionResult,
} from '../lib/types'
import {
  expandUrl,
  isValidVideoUrl,
  extractVideoId,
  getPlatformNameFromUrl,
} from '../lib/utils'

export function useTranscription() {
  const [url, setUrl] = useState<string>('')
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const [isExpanding, setIsExpanding] = useState<boolean>(false)
  const [currentTask, setCurrentTask] = useState<TranscriptionResponse | null>(
    null,
  )
  const [error, setError] = useState<string | null>(null)
  const [transcript, setTranscript] = useState<TranscriptionResult | null>(null)
  const [progress, setProgress] = useState<number>(0)
  const [startTime, setStartTime] = useState<number | null>(null)
  const [platform, setPlatform] = useState<string>('')
  const [retryCount, setRetryCount] = useState<number>(0)
  const [isRetrying, setIsRetrying] = useState<boolean>(false)

  useEffect(() => {
    setError(null)
    if (url) {
      setPlatform(getPlatformNameFromUrl(url))
    } else {
      setPlatform('')
    }
  }, [url])

  useEffect(() => {
    let intervalId: NodeJS.Timeout
    let failedAttempts = 0
    const MAX_FAILED_ATTEMPTS = 5
    const checkTaskStatus = async () => {
      if (!currentTask || !isProcessing) return
      try {
        const taskStatus = await getTaskStatus(currentTask.task_id)
        // Reset failed attempts counter on success
        failedAttempts = 0
        setCurrentTask(taskStatus)
        switch (taskStatus.status) {
          case 'pending':
            // Still waiting to start
            setProgress(10)
            break
          case 'processing':
            // In progress - simulate progress between 20-90%
            setProgress(Math.min(90, Math.max(20, progress + 5)))
            break
          case 'completed':
            // Completed successfully
            setProgress(100)
            setIsProcessing(false)
            setStartTime(null)
            try {
              const rawTranscript = await getTranscript(taskStatus.task_id)
              const parsedTranscript = parseTranscript(rawTranscript)
              setTranscript(parsedTranscript)
            } catch (transcriptErr) {
              console.error('Error fetching transcript:', transcriptErr)
              setError('Transcription completed but failed to fetch transcript')
            }
            break
          case 'failed':
            // Failed with error
            setIsProcessing(false)
            setStartTime(null)
            setError(taskStatus.error || 'Failed to download or process video')
            break
          default:
            // Unknown status
            console.warn('Unknown task status:', taskStatus.status)
        }
      } catch (err) {
        failedAttempts++
        console.error(
          `Error checking task status (attempt ${failedAttempts}/${MAX_FAILED_ATTEMPTS}):`,
          err,
        )
        // If we've failed too many times, stop polling and show error
        if (failedAttempts >= MAX_FAILED_ATTEMPTS) {
          setIsProcessing(false)
          setStartTime(null)
          setError(
            'Connection to the transcription service was lost. Your transcription may still be processing in the background. Please check the transcriptions list later.',
          )
        }
        // Otherwise continue polling - the error might be temporary
      }
    }
    if (isProcessing && currentTask) {
      // Initial check
      checkTaskStatus()
      // Poll every 5 seconds as recommended in the API guide
      intervalId = setInterval(checkTaskStatus, 5000)
    }
    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [isProcessing, currentTask, progress])

  const startTranscription = async () => {
    if (!url.trim()) {
      setError('Please enter a valid video URL')
      return
    }
    if (!isValidVideoUrl(url)) {
      setError('Please enter a valid TikTok URL')
      return
    }
    try {
      setIsProcessing(true)
      setIsExpanding(true)
      setProgress(0)
      setTranscript(null)
      setError(null)
      setStartTime(Date.now())
      setRetryCount(0)
      setIsRetrying(false)
      // Ensure URL has protocol
      let finalUrl = url.trim()
      if (!finalUrl.startsWith('http')) {
        finalUrl = `https://${finalUrl}`
      }
      console.log('Initial URL:', finalUrl)
      // For TikTok URLs, we might still want to expand short URLs
      if (getPlatformNameFromUrl(finalUrl) === 'TikTok') {
        try {
          console.log('Attempting to expand short URL...')
          const expandedUrl = await expandUrl(finalUrl)
          finalUrl = expandedUrl
          console.log('Expanded URL:', expandedUrl)
        } catch (err) {
          console.warn('URL expansion failed, using original URL:', err)
          // Continue with original URL, the API should handle it
        }
      }
      setIsExpanding(false)
      // Create the request object following API guidelines
      const request: TranscriptionRequest = {
        url: finalUrl,
      }
      console.log('Sending transcription request:', request)
      const response = await transcribeVideo(request)
      setCurrentTask(response)
      // Initialize progress based on status
      if (response.status === 'failed') {
        setIsProcessing(false)
        setStartTime(null)
        throw new Error(response.error || 'Failed to process video')
      } else if (response.status === 'completed') {
        setProgress(100)
        setIsProcessing(false)
        setStartTime(null)
        const rawTranscript = await getTranscript(response.task_id)
        const parsedTranscript = parseTranscript(rawTranscript)
        setTranscript(parsedTranscript)
      } else {
        // For 'pending' or 'processing' status, we'll continue polling
        setProgress(response.status === 'pending' ? 10 : 20)
      }
    } catch (err) {
      console.error('Error starting transcription:', err)
      let errorMessage =
        'Failed to start transcription. Please try again later.'
      if (err instanceof Error) {
        // Provide more user-friendly error messages
        if (err.message.includes('429')) {
          errorMessage =
            'Too many requests. Please wait a moment and try again.'
        } else if (err.message.includes('404')) {
          errorMessage = 'Video not found. Please check the URL and try again.'
        } else if (err.message.includes('400')) {
          errorMessage = 'Invalid request. Please check the URL format.'
        } else if (err.message.includes('500')) {
          errorMessage =
            'Server error. The transcription service is experiencing issues.'
        } else {
          errorMessage = err.message
        }
      }
      setError(errorMessage)
      setIsProcessing(false)
      setIsExpanding(false)
      setStartTime(null)
    }
  }

  const stopTranscription = async (taskId: string) => {
    if (!taskId) return
    try {
      setError(null)
      const result = await stopTranscription(taskId)
      // Update the current task if it's the one being stopped
      if (currentTask && currentTask.task_id === taskId) {
        setIsProcessing(false)
        setStartTime(null)
        setCurrentTask({
          ...currentTask,
          status: 'cancelled',
        })
      }
      return result
    } catch (err) {
      console.error('Error stopping transcription:', err)
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to stop transcription. Please try again.'
      setError(errorMessage)
      return false
    }
  }

  const retryTranscription = () => {
    if (retryCount >= 3) {
      setError('Maximum retry attempts reached. Please try again later.')
      return
    }
    setIsRetrying(true)
    setRetryCount((prev) => prev + 1)
    setError(null)
    // Wait a bit before retrying to allow any temporary issues to resolve
    setTimeout(() => {
      startTranscription()
      setIsRetrying(false)
    }, 1500)
  }

  return {
    url,
    setUrl,
    isProcessing,
    isExpanding,
    progress,
    error,
    transcript,
    currentTask,
    startTranscription,
    stopTranscription,
    retryTranscription,
    isRetrying,
    retryCount,
    startTime,
    platform,
  }
}
